import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminSalesService, PosService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { calculateServiceCharge } from "../../utils/saleFinancials";
import { getBarProductMeasurementLabel, isCigaretteBarProduct } from "../../utils/barProducts";
import {
    ArrowLeft,
    Banknote,
    CreditCard,
    Loader2,
    Minus,
    Plus,
    Save,
    Search,
    ShoppingCart,
    Trash2,
    Utensils,
    Wine,
    Coffee,
    X,
} from "lucide-react";
import { toast } from "sonner";

const BOTTLE_SALE_MODES = {
    FULL_BOTTLE: "FULL_BOTTLE",
    MEASURE_180ML: "MEASURE_180ML",
    MEASURE_90ML: "MEASURE_90ML",
};

const RESTAURANT_ITEM_SECTIONS = {
    FOOD: "FOOD",
    BEVERAGE: "BEVERAGE",
    GOODS: "GOODS",
};

const POS_TABS = {
    BAR: "BOTTLE",
    BEVERAGE: "BEVERAGE",
    FOOD: "FOOD",
};

const PAYMENT_MODES = {
    CASH: "CASH",
    CARD: "CARD",
    SPLIT: "SPLIT",
};

const getPaymentModeLabel = (paymentMode) => {
    if (paymentMode === PAYMENT_MODES.CARD) {
        return "Card";
    }

    if (paymentMode === PAYMENT_MODES.SPLIT) {
        return "Split";
    }

    return "Cash";
};

const resolveRestaurantItemType = (product) => {
    if (product.itemSection === RESTAURANT_ITEM_SECTIONS.BEVERAGE) {
        return "BEVERAGE";
    }

    if (product.itemSection === RESTAURANT_ITEM_SECTIONS.GOODS) {
        return "GOODS";
    }

    return "FOOD";
};

const formatContainerAndVolume = (product) => {
    return getBarProductMeasurementLabel(product);
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const normalizeSearchText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\brs\.?/g, " ")
    .replace(/\blkr\b/g, " ")
    .replace(/[,]/g, "")
    .replace(/[^a-z0-9.]+/g, " ")
    .trim();

const getPriceSearchValues = (value) => {
  if (value === null || value === undefined || value === "") {
    return [];
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return [];
  }

  return [
    numericValue,
    numericValue.toFixed(2),
    currency(numericValue),
  ];
};

const createCartKey = (
    itemType,
    itemId,
    bottleSaleMode = BOTTLE_SALE_MODES.FULL_BOTTLE
) => {
    if (itemType === "BOTTLE") {
        return `BOTTLE-${itemId}-${bottleSaleMode || BOTTLE_SALE_MODES.FULL_BOTTLE
            }`;
    }

    return `RESTAURANT-${itemId}`;
};

const isRestaurantSaleItemType = (itemType) =>
    itemType === "BITE" ||
    itemType === "FOOD" ||
    itemType === "BEVERAGE" ||
    itemType === "GOODS";

const isStockTrackedRestaurantItem = (product) =>
    Boolean(product?.stockTracked);

const getRestaurantAvailableQuantity = (product) =>
    Number(product?.availableQuantity || 0);

const getRestaurantLowStockLevel = (product) =>
    Number(product?.lowStockLevel || 0);

const getOriginalRestaurantSaleQuantity = (saleItems, product) =>
    (saleItems || [])
        .filter(
            (item) =>
                isRestaurantSaleItemType(item.itemType) &&
                Number(item.itemId) === Number(product?.biteId)
        )
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

const getRestaurantCorrectionMaxQuantity = (product, saleItems) => {
    if (!isStockTrackedRestaurantItem(product)) {
        return 9999;
    }

    return (
        getRestaurantAvailableQuantity(product) +
        getOriginalRestaurantSaleQuantity(saleItems, product)
    );
};


export const ManualSaleCorrectionPage = () => {
    const { saleId } = useParams();
    const navigate = useNavigate();

    const [sale, setSale] = useState(null);
    const isRestaurantSale = sale?.outletCode === "RESTAURANT";
    const [bottles, setBottles] = useState([]);
    const [bites, setBites] = useState([]);
    const [cart, setCart] = useState([]);

    const [tab, setTab] = useState(POS_TABS.BAR);
    const [search, setSearch] = useState("");

    const [discount, setDiscount] = useState(0);
    const [serviceEnabled, setServiceEnabled] = useState(false);
    const [tax, setTax] = useState(0);
    const [cashAmount, setCashAmount] = useState(0);
    const [cardAmount, setCardAmount] = useState(0);
    const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.CASH);
    const [paymentRef, setPaymentRef] = useState("");
    const [paymentTouched, setPaymentTouched] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentDraftMode, setPaymentDraftMode] = useState(PAYMENT_MODES.CASH);
    const [paymentDraftCashAmount, setPaymentDraftCashAmount] = useState(0);
    const [paymentDraftCardAmount, setPaymentDraftCardAmount] = useState(0);
    const [paymentDraftRef, setPaymentDraftRef] = useState("");
    const [reason, setReason] = useState("");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [measureBottleModal, setMeasureBottleModal] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [customItemModalOpen, setCustomItemModalOpen] = useState(false);
    const [customItemForm, setCustomItemForm] = useState({
        itemSection: RESTAURANT_ITEM_SECTIONS.FOOD,
        itemName: "",
        unitPrice: "",
        quantity: 1,
    });

    const mapSaleItemsToCart = (items, bottleList, biteList, outletCode) => {
        if (outletCode === "RESTAURANT") {
            if ((items || []).some((item) => item.itemType === "BOTTLE")) {
                throw new Error(
                    "This Restaurant sale contains a bottle item and cannot be edited as a Restaurant correction."
                );
            }

            const missingProducts = (items || []).filter((item) => {
                const isCustomItem =
                    isRestaurantSaleItemType(item.itemType) &&
                    (Boolean(item.customItem) || item.itemId == null);
                if (!isRestaurantSaleItemType(item.itemType) || isCustomItem) {
                    return false;
                }
                return !biteList.some(
                    (bite) => Number(bite.biteId) === Number(item.itemId)
                );
            });

            if (missingProducts.length > 0) {
                const names = missingProducts
                    .map((item) => item.itemName)
                    .filter(Boolean)
                    .join(", ");
                throw new Error(
                    `Unable to match existing Restaurant item${missingProducts.length === 1 ? "" : "s"} to the current correction catalog${names ? `: ${names}` : "."} Editing is blocked to prevent an incorrect correction.`
                );
            }

            const productsMissingPrice = (items || []).filter((item) => {
                const isCustomItem =
                    isRestaurantSaleItemType(item.itemType) &&
                    (Boolean(item.customItem) || item.itemId == null);
                if (!isRestaurantSaleItemType(item.itemType) || isCustomItem) {
                    return false;
                }
                const product = biteList.find(
                    (bite) => Number(bite.biteId) === Number(item.itemId)
                );
                return (
                    !product ||
                    product.sellingPrice == null ||
                    !Number.isFinite(Number(product.sellingPrice))
                );
            });

            if (productsMissingPrice.length > 0) {
                throw new Error(
                    "The Restaurant correction catalog is missing a valid current price for an existing product. Editing is blocked to prevent an incorrect correction."
                );
            }
        }

        return (items || []).map((item, index) => {
            const isRestaurantItem =
                isRestaurantSaleItemType(item.itemType);

            const isCustomRestaurantItem =
                isRestaurantItem &&
                (Boolean(item.customItem) || item.itemId == null);

            const bottleSaleMode =
                item.itemType === "BOTTLE"
                    ? item.bottleSaleMode || BOTTLE_SALE_MODES.FULL_BOTTLE
                    : null;

            const bottleProduct =
                item.itemType === "BOTTLE"
                    ? bottleList.find((bottle) => bottle.bottleId === item.itemId)
                    : null;

            const biteProduct =
                isRestaurantItem && !isCustomRestaurantItem
                    ? biteList.find(
                          (bite) => Number(bite.biteId) === Number(item.itemId)
                      )
                    : null;

            const isReturnableBottle =
                item.itemType === "BOTTLE" && item.barProductType !== "CIGARETTE" &&
                Boolean(bottleProduct?.isReturnableBottle);

            const bottleReturnCredit = isReturnableBottle
                ? Number(bottleProduct?.bottleReturnCredit || 0)
                : Number(item.bottleReturnUnitCredit || 0);

            const maxQuantity =
                biteProduct && isStockTrackedRestaurantItem(biteProduct)
                    ? getRestaurantCorrectionMaxQuantity(biteProduct, items)
                    : 9999;

            return {
                key: isCustomRestaurantItem
                    ? `CUSTOM-RESTAURANT-${item.saleItemId || index}`
                    : createCartKey(item.itemType, item.itemId, bottleSaleMode),
                itemType: item.itemType,
                itemId: isCustomRestaurantItem ? null : item.itemId,
                name: item.itemName,
                unitPrice:
                    outletCode === "RESTAURANT" && biteProduct
                        ? Number(biteProduct.sellingPrice)
                        : Number(item.unitPrice || 0),
                quantity: Number(item.quantity || 0),
                max: maxQuantity,
                stockTracked: Boolean(biteProduct?.stockTracked),
                availableQuantity: Number(biteProduct?.availableQuantity || 0),
                lowStockLevel: Number(biteProduct?.lowStockLevel || 0),
                customItem: isCustomRestaurantItem,
                customItemName: isCustomRestaurantItem ? item.itemName : null,
                customUnitPrice: isCustomRestaurantItem
                    ? Number(item.unitPrice || 0)
                    : null,
                isReturnableBottle,
                bottleReturnCredit,
                returnedBottleQuantity: Number(item.returnedBottleQuantity || 0),
                bottleReturned: Number(item.returnedBottleQuantity || 0) > 0,
                bottleSaleMode,
            };
        });
    };

    useEffect(() => {
        let cancelled = false;

        const loadCorrectionData = async () => {
            setLoading(true);
            setError(null);
            setSale(null);
            setBottles([]);
            setBites([]);
            setCart([]);

            try {
                const saleData = await AdminSalesService.detail(saleId);
                const outletCode = saleData?.outletCode;

                if (outletCode !== "BAR" && outletCode !== "RESTAURANT") {
                    throw new Error(
                        "Cannot load manual correction: the sale has no supported outlet (BAR or RESTAURANT)."
                    );
                }

                if (cancelled) return;
                setSale(saleData);
                setTab(outletCode === "BAR" ? POS_TABS.BAR : POS_TABS.FOOD);

                const [bottleData, biteData] =
                    outletCode === "BAR"
                        ? await Promise.all([
                              PosService.bottles(),
                              PosService.bites(),
                          ])
                        : [
                              [],
                              await AdminSalesService.correctionBites(saleId),
                          ];

                const loadedBottles = bottleData || [];
                const loadedBites = biteData || [];
                const initialCart = mapSaleItemsToCart(
                    saleData.items || [],
                    loadedBottles,
                    loadedBites,
                    outletCode
                );

                if (cancelled) return;
                setBottles(loadedBottles);
                setBites(loadedBites);
                setCart(initialCart);
                setDiscount(Number(saleData.discountAmount || 0));
                setServiceEnabled(Number(saleData.serviceChargeAmount || 0) > 0);
                setTax(Number(saleData.taxAmount || 0));

                const payments = saleData.payments || [];
                const cashTotal = payments
                    .filter((payment) => payment.paymentMethod === "CASH")
                    .reduce(
                        (sum, payment) => sum + Number(payment.amount || 0),
                        0
                    );
                const cardPayments = payments.filter(
                    (payment) => payment.paymentMethod === "CARD"
                );
                const cardTotal = cardPayments.reduce(
                    (sum, payment) => sum + Number(payment.amount || 0),
                    0
                );

                setCashAmount(cashTotal);
                setCardAmount(cardTotal);
                setPaymentRef(cardPayments[0]?.referenceNumber || "");

                if (cashTotal > 0 && cardTotal > 0) {
                    setPaymentMode("SPLIT");
                } else if (cardTotal > 0) {
                    setPaymentMode("CARD");
                } else {
                    setPaymentMode("CASH");
                }
                setPaymentTouched(false);
            } catch (e) {
                console.error("Failed to load manual correction data", e);
                if (!cancelled) {
                    setError(
                        e?.response?.data?.message ||
                            e?.message ||
                            "Failed to load sale"
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadCorrectionData();
        return () => {
            cancelled = true;
        };
    }, [saleId]);

    const filteredItems = useMemo(() => {
        const q = normalizeSearchText(search);

        const decorateBottle = (product) => ({
            ...product,
            __posType: "BOTTLE",
            __posTab: POS_TABS.BAR,
        });

        const decorateRestaurantItem = (product) => {
            const section = product.itemSection || RESTAURANT_ITEM_SECTIONS.FOOD;

            return {
                ...product,
                __posType: "RESTAURANT",
        __posTab:
                    section === RESTAURANT_ITEM_SECTIONS.BEVERAGE
                        ? POS_TABS.BEVERAGE
                        : POS_TABS.FOOD,
            };
        };

        const decoratedBottles = bottles.map(decorateBottle);
        const decoratedRestaurantItems = bites.map(decorateRestaurantItem);

        const stockSortedBottles = (items) =>
            [...items].sort((a, b) => {
                const aSealedQuantity = Number(a.availableQuantity || 0);
                const bSealedQuantity = Number(b.availableQuantity || 0);

                const aOpenedUnits = Number(a.availableMeasureUnits || 0);
                const bOpenedUnits = Number(b.availableMeasureUnits || 0);

                const aHasStock = a.barProductType !== "CIGARETTE" && a.allowMeasureSale
                    ? aSealedQuantity > 0 || aOpenedUnits > 0
                    : aSealedQuantity > 0;

                const bHasStock = b.barProductType !== "CIGARETTE" && b.allowMeasureSale
                    ? bSealedQuantity > 0 || bOpenedUnits > 0
                    : bSealedQuantity > 0;

                if (aHasStock !== bHasStock) {
                    return aHasStock ? -1 : 1;
                }

                const aTotalStockValue = aSealedQuantity + aOpenedUnits;
                const bTotalStockValue = bSealedQuantity + bOpenedUnits;

                return bTotalStockValue - aTotalStockValue;
            });

        const productMatchesSearch = (product) => {
            if (!q) {
                return true;
            }

            const restaurantRemainingQuantity =
                product.__posType === "RESTAURANT"
                    ? getRestaurantCorrectionRemainingQuantity(product)
                    : null;

            const searchableValues =
                product.__posType === "BOTTLE"
                    ? [
                        "bar",
                        "liquor",
                        "bottle",
                        product.barcode,
                        product.bottleName,
                        product.bottleTypeName,
                        product.companyName,
                        product.bottleSize,
                        product.volumeMl,
                        formatContainerAndVolume(product),
                        product.availableQuantity,
                        product.availableMeasureUnits,
                        product.lowStockLevel,
                        ...getPriceSearchValues(product.sellingPrice),
                        ...getPriceSearchValues(product.measure180Price),
                        ...getPriceSearchValues(product.measure90Price),
                        ...getPriceSearchValues(product.bottleReturnCredit),
                    ]
                    : [
                        "restaurant",
                        product.itemSection === RESTAURANT_ITEM_SECTIONS.BEVERAGE
                            ? "beverage"
                            : "food bites",
                        product.barcode,
                        product.biteName,
                        product.biteCategoryName,
                        product.itemSection,
                        product.stockTracked ? "stock tracked" : "stock optional",
                        product.availableQuantity,
                        restaurantRemainingQuantity,
                        product.lowStockLevel,
                        ...getPriceSearchValues(product.sellingPrice),
                    ];

            return searchableValues
                .filter((value) => value !== null && value !== undefined)
                .some((value) => normalizeSearchText(value).includes(q));
        };

        if (q) {
            return [
                ...(sale?.outletCode === "BAR"
                    ? stockSortedBottles(decoratedBottles)
                    : []),
                ...decoratedRestaurantItems,
            ].filter(productMatchesSearch);
        }

        if (sale?.outletCode === "BAR" && tab === POS_TABS.BAR) {
            return stockSortedBottles(decoratedBottles);
        }

        if (tab === POS_TABS.BEVERAGE) {
            return decoratedRestaurantItems.filter(
                (item) => item.itemSection === RESTAURANT_ITEM_SECTIONS.BEVERAGE
            );
        }

        return decoratedRestaurantItems.filter((item) => {
            const section = item.itemSection || RESTAURANT_ITEM_SECTIONS.FOOD;

            return (
                section === RESTAURANT_ITEM_SECTIONS.FOOD ||
                section === RESTAURANT_ITEM_SECTIONS.GOODS
            );
        });
    }, [tab, bottles, bites, search, cart, sale]);

    const getTotalMeasureUnits = (product) => {
        const availableOpenedUnits = Number(product.availableMeasureUnits || 0);
        const availableSealedUnits = Number(product.availableQuantity || 0) * 8;

        return availableOpenedUnits + availableSealedUnits;
    };

    const getMeasuredMaximumQuantity = (product, saleMode) => {
        const totalUnits = getTotalMeasureUnits(product);

        if (saleMode === BOTTLE_SALE_MODES.MEASURE_180ML) {
            return Math.floor(totalUnits / 2);
        }

        if (saleMode === BOTTLE_SALE_MODES.MEASURE_90ML) {
            return totalUnits;
        }

        return Number(product.availableQuantity || 0);
    };

    const hasAnyBottleStock = (product) => {
        const sealedQuantity = Number(product.availableQuantity || 0);

        if (product.barProductType === "CIGARETTE" || !product.allowMeasureSale) {
            return sealedQuantity > 0;
        }

        const openedUnits = Number(product.availableMeasureUnits || 0);

        return sealedQuantity > 0 || openedUnits > 0;
    };

    const getCurrentRestaurantCartQuantity = (biteId) =>
        cart
            .filter(
                (item) =>
                    isRestaurantSaleItemType(item.itemType) &&
                    Number(item.itemId) === Number(biteId)
            )
            .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const getRestaurantCorrectionRemainingQuantity = (product) => {
        if (!isStockTrackedRestaurantItem(product)) {
            return null;
        }

        const maximumQuantity = getRestaurantCorrectionMaxQuantity(
            product,
            sale?.items || []
        );

        const currentCartQuantity = getCurrentRestaurantCartQuantity(
            product.biteId
        );

        return Math.max(0, maximumQuantity - currentCartQuantity);
    };

    const handleBottleClick = (product) => {
        if (isRestaurantSale) return;

        if (!isCigaretteBarProduct(product) && product.allowMeasureSale) {
            setMeasureBottleModal(product);
            return;
        }

        addItem(product, "BOTTLE", BOTTLE_SALE_MODES.FULL_BOTTLE);
    };

    const handleProductSelection = (product, out) => {
        if (out) {
            return;
        }

        const productTab = product.__posTab || tab;

        if (productTab !== tab) {
            setTab(productTab);
        }

        if (search.trim()) {
            setSearch("");
        }

        if (product.__posType === "BOTTLE") {
            handleBottleClick(product);
            return;
        }

        addItem(product, resolveRestaurantItemType(product));
    };

    const openCustomItemModal = () => {
        const defaultSection =
            tab === POS_TABS.BEVERAGE
                ? RESTAURANT_ITEM_SECTIONS.BEVERAGE
                : RESTAURANT_ITEM_SECTIONS.FOOD;

        setCustomItemForm({
            itemSection: defaultSection,
            itemName: "",
            unitPrice: "",
            quantity: 1,
        });

        setCustomItemModalOpen(true);
    };

    const updateCustomItemForm = (field, value) => {
        setCustomItemForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const addCustomRestaurantItem = () => {
        const itemName = customItemForm.itemName.trim();
        const unitPrice = Number(customItemForm.unitPrice || 0);
        const quantity = Math.max(
            1,
            Math.floor(Number(customItemForm.quantity || 1))
        );

        if (!itemName) {
            return toast.error("Custom item name is required");
        }

        if (unitPrice <= 0) {
            return toast.error("Custom item price must be greater than zero");
        }

        const itemSection =
            customItemForm.itemSection || RESTAURANT_ITEM_SECTIONS.FOOD;

        const itemType =
            itemSection === RESTAURANT_ITEM_SECTIONS.BEVERAGE
                ? "BEVERAGE"
                : itemSection === RESTAURANT_ITEM_SECTIONS.GOODS
                    ? "GOODS"
                    : "FOOD";

        const key = `CUSTOM-RESTAURANT-${Date.now()}`;

        setCart([
            ...cart,
            {
                key,
                itemType,
                itemId: null,
                name: itemName,
                unitPrice,
                quantity,
                max: 9999,
                stockTracked: false,
                availableQuantity: 0,
                lowStockLevel: 0,
                customItem: true,
                customItemName: itemName,
                customUnitPrice: unitPrice,
                isReturnableBottle: false,
                bottleReturnCredit: 0,
                returnedBottleQuantity: 0,
                bottleReturned: false,
                bottleSaleMode: null,
            },
        ]);

        setCustomItemModalOpen(false);
        setCustomItemForm({
            itemSection: RESTAURANT_ITEM_SECTIONS.FOOD,
            itemName: "",
            unitPrice: "",
            quantity: 1,
        });
    };

    const addItem = (
        product,
        itemType = tab,
        bottleSaleMode = BOTTLE_SALE_MODES.FULL_BOTTLE
    ) => {
        if (itemType === "BOTTLE") {
            if (isRestaurantSale) return;

            if (isCigaretteBarProduct(product)
                && bottleSaleMode !== BOTTLE_SALE_MODES.FULL_BOTTLE) {
                return toast.error("Cigarettes can only be sold as whole units");
            }

            const isMeasuredSale =
                bottleSaleMode !== BOTTLE_SALE_MODES.FULL_BOTTLE;

            const maximumQuantity = isMeasuredSale
                ? getMeasuredMaximumQuantity(product, bottleSaleMode)
                : Number(product.availableQuantity || 0);

            if (maximumQuantity <= 0) {
                return toast.error(
                    isMeasuredSale ? "No measured stock is available" : "Out of stock"
                );
            }

            let unitPrice = Number(product.sellingPrice);

            if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_180ML) {
                unitPrice = Number(product.measure180Price);
            } else if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_90ML) {
                unitPrice = Number(product.measure90Price);
            }

            const key = createCartKey("BOTTLE", product.bottleId, bottleSaleMode);

            const found = cart.find((item) => item.key === key);

            if (found) {
                if (found.quantity >= maximumQuantity) {
                    return toast.error("Reached available stock");
                }

                setCart(
                    cart.map((item) =>
                        item.key === key
                            ? {
                                ...item,
                                quantity: item.quantity + 1,
                                max: maximumQuantity,
                            }
                            : item
                    )
                );
            } else {
                let measuredLabel = "";

                if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_180ML) {
                    measuredLabel = " - 180 ml";
                } else if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_90ML) {
                    measuredLabel = " - 90 ml";
                }

                setCart([
                    ...cart,
                    {
                        key,
                        itemType: "BOTTLE",
                        itemId: product.bottleId,
                        name: isCigaretteBarProduct(product)
                            ? product.bottleName
                            : `${product.bottleName} ${formatContainerAndVolume(product)}${measuredLabel}`,
                        barProductType: product.barProductType || "LIQUOR",
                        unitPrice,
                        quantity: 1,
                        max: maximumQuantity,
                        isReturnableBottle: !isCigaretteBarProduct(product) && Boolean(product.isReturnableBottle),
                        bottleReturnCredit: isCigaretteBarProduct(product) ? 0 : Number(product.bottleReturnCredit || 0),
                        returnedBottleQuantity: 0,
                        bottleReturned: false,
                        bottleSaleMode,
                    },
                ]);
            }

            setMeasureBottleModal(null);
            return;
        }

        const restaurantItemType =
            itemType === "BITE" ? resolveRestaurantItemType(product) : itemType;

        const maximumQuantity = getRestaurantCorrectionMaxQuantity(
            product,
            sale?.items || []
        );

        if (isStockTrackedRestaurantItem(product) && maximumQuantity <= 0) {
            return toast.error("Out of stock");
        }

        const key = createCartKey(restaurantItemType, product.biteId);
        const found = cart.find((item) => item.key === key);

        if (found) {
            if (found.quantity >= maximumQuantity) {
                return toast.error("Reached available stock");
            }

            setCart(
                cart.map((item) =>
                    item.key === key
                        ? {
                            ...item,
                            quantity: item.quantity + 1,
                            max: maximumQuantity,
                            itemType: restaurantItemType,
                            stockTracked: Boolean(product.stockTracked),
                            availableQuantity: Number(product.availableQuantity || 0),
                            lowStockLevel: Number(product.lowStockLevel || 0),
                        }
                        : item
                )
            );
        } else {
            setCart([
                ...cart,
                {
                    key,
                    itemType: restaurantItemType,
                    itemId: product.biteId,
                    name: product.biteName,
                    unitPrice: Number(product.sellingPrice),
                    quantity: 1,
                    max: maximumQuantity,
                    stockTracked: Boolean(product.stockTracked),
                    availableQuantity: Number(product.availableQuantity || 0),
                    lowStockLevel: Number(product.lowStockLevel || 0),
                    isReturnableBottle: false,
                    bottleReturnCredit: 0,
                    returnedBottleQuantity: 0,
                    bottleReturned: false,
                    bottleSaleMode: null,
                },
            ]);
        }
    };

    const incr = (key) => {
        const found = cart.find((item) => item.key === key);

        if (!found) {
            return;
        }

        if (found.quantity >= found.max) {
            return toast.error("Reached available stock");
        }

        setCart(
            cart.map((item) =>
                item.key === key
                    ? {
                        ...item,
                        quantity: item.quantity + 1,
                    }
                    : item
            )
        );
    };

    const decr = (key) =>
        setCart(
            cart.map((item) =>
                item.key === key
                    ? {
                        ...item,
                        quantity: Math.max(1, item.quantity - 1),
                    }
                    : item
            )
        );

    const updateReturnedBottleQuantity = (key, value) => {
        setCart(
            cart.map((item) => {
                if (item.key !== key) {
                    return item;
                }

                const requestedQuantity = Number(value || 0);

                const returnedBottleQuantity = Math.max(
                    0,
                    Math.floor(requestedQuantity)
                );

                return {
                    ...item,
                    returnedBottleQuantity,
                    bottleReturned: returnedBottleQuantity > 0,
                };
            })
        );
    };

    const adjustReturnedBottleQuantity = (key, amount) => {
        const item = cart.find((cartItem) => cartItem.key === key);

        if (!item) {
            return;
        }

        const nextReturnedBottleQuantity =
            Number(item.returnedBottleQuantity || 0) + Number(amount || 0);

        updateReturnedBottleQuantity(key, nextReturnedBottleQuantity);
    };

    const remove = (key) => {
        setCart(cart.filter((item) => item.key !== key));
    };

    const getCartItemAmounts = (item) => {
        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(item.unitPrice || 0);

        const returnedBottleQuantity =
            item.itemType === "BOTTLE" && item.barProductType !== "CIGARETTE" && item.isReturnableBottle
                ? Number(item.returnedBottleQuantity || 0)
                : 0;

        const bottleReturnCredit = Number(item.bottleReturnCredit || 0);

        const grossAmount = quantity * unitPrice;
        const returnCreditAmount = returnedBottleQuantity * bottleReturnCredit;

        return {
            grossAmount,
            returnCreditAmount,
            netAmount: grossAmount - returnCreditAmount,
        };
    };

    const subTotal = cart.reduce(
        (sum, item) => sum + getCartItemAmounts(item).netAmount,
        0
    );

    const barSubtotal = cart.reduce((sum, item) => sum + (item.itemType === "BOTTLE" ? getCartItemAmounts(item).netAmount : 0), 0);
    const restaurantSubtotal = roundMoney(subTotal - barSubtotal);
    const isBarSale = sale?.outletCode === "BAR";
    const serviceAmount = calculateServiceCharge(restaurantSubtotal, discount, isBarSale, serviceEnabled);
    const appliedTax = isBarSale && barSubtotal <= 0 ? 0 : Number(tax || 0);
    const grandTotal = roundMoney(subTotal - Number(discount || 0) + serviceAmount + appliedTax);

    const cashPaymentAmount = roundMoney(cashAmount);
    const cardPaymentAmount = roundMoney(cardAmount);
    const totalPaidAmount = roundMoney(cashPaymentAmount + cardPaymentAmount);
    const balanceAmount = roundMoney(grandTotal - totalPaidAmount);
    const isPaymentBalanced = Math.abs(balanceAmount) < 0.01;

    const draftCashPaymentAmount = roundMoney(paymentDraftCashAmount);
    const draftCardPaymentAmount = roundMoney(paymentDraftCardAmount);
    const draftTotalPaidAmount = roundMoney(
        draftCashPaymentAmount + draftCardPaymentAmount
    );
    const draftBalanceAmount = roundMoney(grandTotal - draftTotalPaidAmount);
    const isDraftPaymentBalanced = Math.abs(draftBalanceAmount) < 0.01;

    useEffect(() => {
        if (paymentMode === "SPLIT" && paymentTouched) {
            return;
        }

        if (paymentMode === "CASH") {
            setCashAmount(grandTotal);
            setCardAmount(0);
            setPaymentRef("");
            return;
        }

        if (paymentMode === "CARD") {
            setCashAmount(0);
            setCardAmount(grandTotal);
        }
    }, [grandTotal, paymentMode, paymentTouched]);

    const openPaymentModal = () => {
        setPaymentDraftMode(paymentMode);
        setPaymentDraftCashAmount(cashPaymentAmount);
        setPaymentDraftCardAmount(cardPaymentAmount);
        setPaymentDraftRef(paymentRef || "");
        setPaymentModalOpen(true);
    };

    const selectDraftPaymentMode = (nextPaymentMode) => {
        setPaymentDraftMode(nextPaymentMode);

        if (nextPaymentMode === PAYMENT_MODES.CASH) {
            setPaymentDraftCashAmount(grandTotal);
            setPaymentDraftCardAmount(0);
            setPaymentDraftRef("");
            return;
        }

        if (nextPaymentMode === PAYMENT_MODES.CARD) {
            setPaymentDraftCashAmount(0);
            setPaymentDraftCardAmount(grandTotal);
            return;
        }

        if (draftTotalPaidAmount <= 0) {
            setPaymentDraftCashAmount(grandTotal);
            setPaymentDraftCardAmount(0);
        }
    };

    const updateDraftCashAmount = (value) => {
        setPaymentDraftCashAmount(value === "" ? "" : Math.max(0, Number(value)));
    };

    const updateDraftCardAmount = (value) => {
        setPaymentDraftCardAmount(value === "" ? "" : Math.max(0, Number(value)));
    };

    const applyPaymentModal = () => {
        if (grandTotal <= 0) {
            setPaymentModalOpen(false);
            return;
        }

        if (paymentDraftMode === PAYMENT_MODES.SPLIT && !isDraftPaymentBalanced) {
            return toast.error("Payment total must be equal to grand total");
        }

        if (paymentDraftMode === PAYMENT_MODES.SPLIT && draftTotalPaidAmount <= 0) {
            return toast.error("Enter at least one payment amount");
        }

        setPaymentMode(paymentDraftMode);
        setCashAmount(draftCashPaymentAmount);
        setCardAmount(draftCardPaymentAmount);
        setPaymentRef(draftCardPaymentAmount > 0 ? paymentDraftRef : "");
        setPaymentTouched(true);
        setPaymentModalOpen(false);
    };

    const selectCashOnlyPayment = () => {
        setPaymentTouched(true);
        setPaymentMode("CASH");
        setCashAmount(grandTotal);
        setCardAmount(0);
        setPaymentRef("");
    };

    const selectCardOnlyPayment = () => {
        setPaymentTouched(true);
        setPaymentMode("CARD");
        setCashAmount(0);
        setCardAmount(grandTotal);
    };

    const selectSplitPayment = () => {
        setPaymentTouched(true);
        setPaymentMode("SPLIT");

        if (totalPaidAmount <= 0) {
            setCashAmount(grandTotal);
            setCardAmount(0);
        }
    };

    const updateCashAmount = (value) => {
        setPaymentTouched(true);
        setPaymentMode("SPLIT");
        setCashAmount(value === "" ? "" : Math.max(0, Number(value)));
    };

    const updateCardAmount = (value) => {
        setPaymentTouched(true);
        setPaymentMode("SPLIT");
        setCardAmount(value === "" ? "" : Math.max(0, Number(value)));
    };

    const resetPaymentBreakdown = () => {
        setPaymentTouched(true);
        setPaymentMode("CASH");
        setCashAmount(grandTotal);
        setCardAmount(0);
        setPaymentRef("");
    };

    const buildPaymentRequests = () => {
        const payments = [];

        if (cashPaymentAmount > 0) {
            payments.push({
                paymentMethod: "CASH",
                amount: cashPaymentAmount,
                referenceNumber: null,
                note: null,
            });
        }

        if (cardPaymentAmount > 0) {
            payments.push({
                paymentMethod: "CARD",
                amount: cardPaymentAmount,
                referenceNumber: paymentRef || null,
                note: null,
            });
        }

        return payments;
    };

    const submit = () => {
        if (!reason.trim()) {
            return toast.error("Correction reason is required");
        }

        if (cart.length === 0) {
            return toast.error("At least one sale item is required");
        }

        if (grandTotal <= 0) {
            return toast.error("Grand total must be greater than 0");
        }

        if (totalPaidAmount <= 0) {
            return toast.error("Enter at least one payment amount");
        }

        if (!isPaymentBalanced) {
            return toast.error("Payment total must be equal to grand total");
        }

        setShowConfirmDialog(true);
    };

    const confirmManualCorrection = async () => {
        setSubmitting(true);

        try {
            await AdminSalesService.manualCorrection(saleId, {
                reason: reason.trim(),
                items: cart.map((item) => ({
                    itemType: item.itemType,
                    itemId: item.customItem ? null : item.itemId,
                    quantity: Number(item.quantity || 0),
                    customItem: Boolean(item.customItem),
                    customItemName: item.customItem
                        ? item.customItemName || item.name
                        : null,
                    customUnitPrice: item.customItem
                        ? Number(item.customUnitPrice || item.unitPrice || 0)
                        : null,
                    bottleSaleMode:
                        item.itemType === "BOTTLE"
                            ? item.bottleSaleMode || BOTTLE_SALE_MODES.FULL_BOTTLE
                            : null,
                    returnedBottleQuantity:
                        item.itemType === "BOTTLE" && item.barProductType !== "CIGARETTE" && item.isReturnableBottle
                            ? Number(item.returnedBottleQuantity || 0)
                            : 0,
                })),
                payments: buildPaymentRequests(),
                discountAmount: Number(discount || 0),
                serviceChargeEnabled: serviceEnabled,
                taxAmount: appliedTax,
            });

            toast.success("Sale corrected successfully");
            navigate(`/admin/sales/${saleId}`);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to correct sale");
        } finally {
            setSubmitting(false);
            setShowConfirmDialog(false);
        }
    };

    if (loading) {
        return (
            <div className="grid place-items-center py-12 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    if (error || !sale) {
        return (
            <div className="max-w-md mx-auto bms-card p-8 text-center">
                    <h2 className="font-display text-xl font-bold text-slate-900">
                        {error ? "Unable to edit sale" : "Sale not found"}
                </h2>

                <p className="text-slate-500 mt-2 text-sm">
                    {error || "This sale no longer exists."}
                </p>

                <Link to="/admin/sales">
                    <Button className="mt-4 bg-navy-800 hover:bg-navy-900 text-white">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <>
            <PageHeader
                title={`Manual Correction - ${sale.saleCode || sale.billNumber}`}
                subtitle={`${dateTime(sale.createdAt || sale.saleDateTime)} · ${sale.cashierName ||
                    sale.cashierFullName ||
                    sale.cashierUsername
                    }`}
                actions={
                    <Link to={`/admin/sales/${saleId}`}>
                        <Button variant="outline">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </Link>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-4 items-start">
                <div className="bms-card p-4 lg:p-5">
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-4">
                        <div className="w-full xl:w-auto overflow-x-auto pb-1">
                            <div className="inline-flex min-w-max items-center gap-1 p-1 bg-slate-100 rounded-lg">
            {!isRestaurantSale && <button
                                onClick={() => {
                                    setTab(POS_TABS.BAR);
                                    setSearch("");
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 ${tab === POS_TABS.BAR
                                        ? "bg-rose-50 shadow text-rose-700"
                                        : "text-slate-600"
                                    }`}
                            >
                                <Wine className="h-4 w-4" /> Liquor
            </button>}

                            <button
                                onClick={() => {
                                    setTab(POS_TABS.BEVERAGE);
                                    setSearch("");
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 ${tab === POS_TABS.BEVERAGE
                                        ? "bg-sky-50 shadow text-sky-700"
                                        : "text-slate-600"
                                    }`}
                            >
                                <Coffee className="h-4 w-4" /> {isRestaurantSale ? "Beverage" : "Beverages"}
                            </button>

                            <button
                                onClick={() => {
                                    setTab(POS_TABS.FOOD);
                                    setSearch("");
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 ${tab === POS_TABS.FOOD
                                        ? "bg-yellow-50 shadow text-yellow-700"
                                        : "text-slate-600"
                                    }`}
                            >
                                <Utensils className="h-4 w-4" /> {isRestaurantSale ? "Food" : "Food & Bites"}
                            </button>
                        </div>

                        </div>

                        <div className="flex w-full xl:flex-1 flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
                            <div className="relative w-full xl:max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                                <Input
                                    placeholder="Search all items…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-9"
                                />
                            </div>

                            {tab !== POS_TABS.BAR && (
                                <Button
                                    type="button"
                                    onClick={openCustomItemModal}
                                    className="h-10 shrink-0 bg-navy-800 hover:bg-navy-900 text-white font-semibold px-4"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Custom Item
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                        {filteredItems.map((product) => {
                            const isBottle = product.__posType === "BOTTLE";
                            const productTab = product.__posTab || tab;
                            const isBeverage = productTab === POS_TABS.BEVERAGE;
                            const id = isBottle ? product.bottleId : product.biteId;

                            const restaurantRemainingQuantity = isBottle
                                ? null
                                : getRestaurantCorrectionRemainingQuantity(product);

                            const restaurantOut =
                                !isBottle &&
                                isStockTrackedRestaurantItem(product) &&
                                restaurantRemainingQuantity <= 0;

                            const restaurantLow =
                                !isBottle &&
                                isStockTrackedRestaurantItem(product) &&
                                restaurantRemainingQuantity > 0 &&
                                restaurantRemainingQuantity <=
                                getRestaurantLowStockLevel(product);

                            const out = isBottle
                                ? !hasAnyBottleStock(product)
                                : restaurantOut;

                            const low = isBottle
                                ? Number(product.availableQuantity || 0) > 0 &&
                                Number(product.availableQuantity || 0) <=
                                Number(product.lowStockLevel || 0)
                                : restaurantLow;

                            const stockStatusClass = out
                                ? "text-rose-600"
                                : low
                                    ? "text-amber-600"
                                    : "text-emerald-600";

                            const stockLabel = isBottle
                                ? out
                                    ? "OUT"
                                    : product.barProductType === "CIGARETTE"
                                        ? `${Number(product.availableQuantity || 0)} units`
                                    : product.allowMeasureSale
                                        ? `${Number(product.availableQuantity || 0)} sealed · ${Number(
                                            product.availableMeasureUnits || 0
                                        )} units`
                                        : `${Number(product.availableQuantity || 0)} sealed`
                                : isStockTrackedRestaurantItem(product)
                                    ? out
                                        ? "OUT"
                                        : `${restaurantRemainingQuantity} left`
                                    : null;

                            return (
                                <button
                                    key={`${product.__posType || productTab}-${id}`}
                                    onClick={() => handleProductSelection(product, out)}
                                    disabled={out}
                                    className={`text-left bms-card p-3 sm:p-4 min-h-[220px] sm:min-h-[258px] hover:border-navy-400 hover:shadow-md transition group flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4 ${
                                        out ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                >
                                    <ProductImageBox
                                        product={product}
                                        isBottle={isBottle}
                                        isBeverage={isBeverage}
                                    />

                                    <div className="min-w-0 flex-1 py-1">
                                        <div className="flex items-start justify-between gap-2 sm:gap-3">
                                            <div className="min-w-0">
                                                <div className="text-sm sm:text-base font-semibold text-slate-900 line-clamp-2 leading-snug">
                                                    {isBottle ? product.bottleName : product.biteName}
                                                </div>

                                                <div className="mt-0.5 text-xs sm:text-sm text-slate-500">
                                                    {isBottle
                                                        ? formatContainerAndVolume(product)
                                                        : product.biteCategoryName}
                                                </div>
                                            </div>

                                            {stockLabel && (
                                                <span
                                                    className={`shrink-0 rounded-full bg-white border border-slate-200 px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-[11px] font-semibold shadow-sm ${stockStatusClass}`}
                                                >
                                                    {stockLabel}
                                                </span>
                                            )}
                                        </div>

                                        {isBottle && product.barProductType !== "CIGARETTE" && product.allowMeasureSale ? (
                                            <div className="mt-2 space-y-0.5">
                                                <div className="font-display font-semibold text-navy-800">
                                                    Bottle: {currency(product.sellingPrice)}
                                                </div>

                                                <div className="text-[11px] text-amber-700 font-semibold">
                                                    180 ml: {currency(product.measure180Price)}
                                                </div>

                                                <div className="text-[11px] text-amber-700 font-semibold">
                                                    90 ml: {currency(product.measure90Price)}
                                                </div>

                                                {product.isReturnableBottle && (
                                                    <div className="text-[11px] text-emerald-700 font-semibold">
                                                        Empty return credit:{" "}
                                                        {currency(product.bottleReturnCredit)}
                                                    </div>
                                                )}

                                                <div className="text-[10px] text-slate-500">
                                                    Open units:{" "}
                                                    {Number(product.availableMeasureUnits || 0)}
                                                </div>
                                            </div>
                                        ) : isBottle && product.barProductType !== "CIGARETTE" && product.isReturnableBottle ? (
                                            <div className="mt-2 space-y-0.5">
                                                <div className="font-display font-semibold text-navy-800">
                                                    {currency(product.sellingPrice)}
                                                </div>

                                                <div className="text-[11px] text-emerald-700 font-semibold">
                                                    Empty return credit:{" "}
                                                    {currency(product.bottleReturnCredit)}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-2 font-display font-semibold text-navy-800">
                                                {currency(product.sellingPrice)}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}

                        {filteredItems.length === 0 && (
                            <div className="col-span-full text-center text-slate-400 py-8">
                                No items.
                            </div>
                        )}
                    </div>
                </div>

                <div className="bms-card p-4 lg:p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                            <ShoppingCart className="h-5 w-5" /> Corrected bill
                        </h3>
                    </div>

                    <div className="divide-y divide-slate-100">
                        {cart.length === 0 && (
                            <div className="text-sm text-slate-400 py-8 text-center">
                                Add corrected sale items.
                            </div>
                        )}

                        {cart.map((item) => {
                            const itemAmounts = getCartItemAmounts(item);

                            return (
                                <div key={item.key} className="py-3 flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-slate-900 truncate">
                                            {item.name}
                                        </div>

                                        {item.customItem && (
                                            <div className="text-xs text-purple-700 font-medium">
                                                Custom restaurant sale
                                            </div>
                                        )}

                                        {!isRestaurantSale && item.itemType === "BOTTLE" && item.barProductType !== "CIGARETTE" &&
                                            item.isReturnableBottle && (
                                                <div className="mt-2 max-w-[180px]">
                                                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">
                                                        Returned empty bottles
                                                    </Label>

                                                    <div className="mt-1 flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                adjustReturnedBottleQuantity(item.key, -1)
                                                            }
                                                            disabled={
                                                                Number(item.returnedBottleQuantity || 0) <= 0
                                                            }
                                                            className="h-8 w-8 shrink-0 rounded-md border border-slate-200 grid place-items-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        >
                                                            <Minus className="h-3.5 w-3.5" />
                                                        </button>

                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            value={item.returnedBottleQuantity ?? 0}
                                                            onChange={(e) =>
                                                                updateReturnedBottleQuantity(
                                                                    item.key,
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="h-8 w-14 text-center"
                                                        />

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                adjustReturnedBottleQuantity(item.key, 1)
                                                            }
                                                            className="h-8 w-8 shrink-0 rounded-md border border-slate-200 grid place-items-center text-slate-600 hover:bg-slate-50"
                                                        >
                                                            <Plus className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>

                                                    <div className="text-[11px] text-emerald-700 mt-1">
                                                        Credit:{" "}
                                                        {currency(
                                                            Number(item.returnedBottleQuantity || 0) *
                                                            Number(item.bottleReturnCredit || 0)
                                                        )}
                                                    </div>

                                                    <div className="text-[11px] text-navy-800">
                                                        {currency(itemAmounts.netAmount)}
                                                    </div>
                                                </div>
                                            )}

                                        {!isRestaurantSale && item.itemType === "BOTTLE" &&
                                            item.bottleSaleMode ===
                                            BOTTLE_SALE_MODES.MEASURE_180ML && (
                                                <div className="text-xs text-amber-700">
                                                    180 ml serving
                                                </div>
                                            )}

                                        {!isRestaurantSale && item.itemType === "BOTTLE" &&
                                            item.bottleSaleMode ===
                                            BOTTLE_SALE_MODES.MEASURE_90ML && (
                                                <div className="text-xs text-amber-700">
                                                    90 ml serving
                                                </div>
                                            )}

                                        <div className="text-xs text-slate-500">
                                            {currency(item.unitPrice)}
                                        </div>

                                        {isRestaurantSaleItemType(item.itemType) &&
                                            item.stockTracked && (
                                                <div className="text-xs text-emerald-700">
                                                    Stock after correction: {Math.max(
                                                        0,
                                                        Number(item.max || 0) -
                                                        Number(item.quantity || 0)
                                                    )} left
                                                </div>
                                            )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => decr(item.key)}
                                            className="h-7 w-7 rounded-md border border-slate-200 grid place-items-center hover:bg-slate-50"
                                        >
                                            <Minus className="h-3.5 w-3.5" />
                                        </button>

                                        <span className="w-6 text-center text-sm font-medium">
                                            {item.quantity}
                                        </span>

                                        <button
                                            onClick={() => incr(item.key)}
                                            className="h-7 w-7 rounded-md border border-slate-200 grid place-items-center hover:bg-slate-50"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                        </button>
                                    </div>

                                    <div className="w-20 text-right font-medium text-sm">
                                        <div>{currency(itemAmounts.netAmount)}</div>

                                        {itemAmounts.returnCreditAmount > 0 && (
                                            <div className="text-[10px] text-emerald-700 font-normal">
                                                -{currency(itemAmounts.returnCreditAmount)}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => remove(item.key)}
                                        className="text-slate-400 hover:text-rose-600"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5 text-sm">
                        <Row label="Sub total" value={currency(subTotal)} />

                        <div className="grid grid-cols-3 gap-2 mt-1">
                            <NumberInput
                                label="Discount"
                                value={discount}
                                onChange={setDiscount}
                            />

                            <NumberInput label="Tax" value={tax} onChange={setTax} />

                            <ServiceChargeToggle enabled={serviceEnabled} onToggle={() => setServiceEnabled((v) => !v)} />
                        </div>

                        <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between">
                            <span className="font-display font-semibold">
                                Grand total
                            </span>

                            <span className="font-display font-bold text-xl text-navy-900">
                                {currency(grandTotal)}
                            </span>
                        </div>
                    </div>

                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                                    Payment
                                </div>
                                <div className="font-semibold text-slate-900">
                                    {getPaymentModeLabel(paymentMode)}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={openPaymentModal}
                                className="text-xs font-semibold text-navy-700 hover:underline"
                            >
                                Change
                            </button>
                        </div>

                        <div className="mt-2 space-y-1 border-t border-slate-200 pt-2 text-xs">
                            {cashPaymentAmount > 0 && (
                                <Row label="Cash" value={currency(cashPaymentAmount)} />
                            )}

                            {cardPaymentAmount > 0 && (
                                <Row label="Card" value={currency(cardPaymentAmount)} />
                            )}

                            {cardPaymentAmount > 0 && paymentRef && (
                                <Row label="Reference" value={paymentRef} />
                            )}

                            {paymentMode === PAYMENT_MODES.SPLIT && (
                                <Row
                                    label="Balance"
                                    value={currency(balanceAmount)}
                                    valueClassName={
                                        isPaymentBalanced ? "text-emerald-700" : "text-rose-600"
                                    }
                                />
                            )}
                        </div>
                    </div>

                    {!isPaymentBalanced && (
                        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
                            Payment total must be equal to the grand total. Click Change to update payment.
                        </div>
                    )}

                    <div className="mt-3">
                        <Label className="text-xs text-slate-500">
                            Correction reason
                        </Label>

                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Example: Wrong bottle selected by cashier"
                            className="mt-1"
                        />
                    </div>

                    <Button
                        onClick={submit}
                        disabled={submitting || cart.length === 0}
                        className="mt-3 w-full h-12 bg-amber-500 hover:bg-amber-600 text-navy-900 font-semibold"
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {submitting
                            ? "Saving correction…"
                            : `Save Correction · ${currency(grandTotal)}`}
                    </Button>
                </div>
            </div>

            {paymentModalOpen && (
              <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90dvh] overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 shrink-0">
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900">
                        Select Payment
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Choose cash, card, or split payment for this correction.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPaymentModalOpen(false)}
                      className="text-slate-400 hover:text-slate-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="p-5 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => selectDraftPaymentMode(PAYMENT_MODES.CASH)}
                        className={`h-12 rounded-xl border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                          paymentDraftMode === PAYMENT_MODES.CASH
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <Banknote className="h-4 w-4" />
                        Cash
                      </button>

                      <button
                        type="button"
                        onClick={() => selectDraftPaymentMode(PAYMENT_MODES.CARD)}
                        className={`h-12 rounded-xl border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                          paymentDraftMode === PAYMENT_MODES.CARD
                            ? "border-navy-500 bg-navy-50 text-navy-800"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <CreditCard className="h-4 w-4" />
                        Card
                      </button>

                      <button
                        type="button"
                        onClick={() => selectDraftPaymentMode(PAYMENT_MODES.SPLIT)}
                        className={`h-12 rounded-xl border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                          paymentDraftMode === PAYMENT_MODES.SPLIT
                            ? "border-amber-400 bg-amber-50 text-amber-700"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <Banknote className="h-4 w-4" />
                        Split
                      </button>
                    </div>

                    {paymentDraftMode === PAYMENT_MODES.CASH && (
                      <PaymentInfoBox
                        icon={<Banknote className="h-4 w-4 text-emerald-700" />}
                        title="Cash payment selected"
                        description="The full bill amount will be recorded as cash."
                      />
                    )}

                    {paymentDraftMode === PAYMENT_MODES.CARD && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <PaymentInfoBox
                          icon={<CreditCard className="h-4 w-4 text-sky-700" />}
                          title="Card payment selected"
                          description="The full bill amount will be recorded as card."
                        />

                        <Input
                          placeholder="Card reference (optional)"
                          value={paymentDraftRef}
                          onChange={(e) => setPaymentDraftRef(e.target.value)}
                          data-testid="pos-card-ref"
                        />
                      </div>
                    )}

                    {paymentDraftMode === PAYMENT_MODES.SPLIT && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                        <div className="text-xs font-semibold text-amber-800">
                          Enter how much was paid by cash and card.
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-600">
                              <Banknote className="h-4 w-4 text-emerald-600" />
                              Cash Amount
                            </div>

                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={paymentDraftCashAmount}
                              onChange={(e) => updateDraftCashAmount(e.target.value)}
                              className="h-9"
                              data-testid="pos-cash-amount"
                            />
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-600">
                              <CreditCard className="h-4 w-4 text-sky-600" />
                              Card Amount
                            </div>

                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={paymentDraftCardAmount}
                              onChange={(e) => updateDraftCardAmount(e.target.value)}
                              className="h-9"
                              data-testid="pos-card-amount"
                            />
                          </div>
                        </div>

                        {draftCardPaymentAmount > 0 && (
                          <Input
                            placeholder="Card reference (optional)"
                            value={paymentDraftRef}
                            onChange={(e) => setPaymentDraftRef(e.target.value)}
                            data-testid="pos-card-ref"
                          />
                        )}
                      </div>
                    )}

                    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm space-y-1">
                      <Row label="Bill total" value={currency(grandTotal)} />
                      <Row label="Total paid" value={currency(draftTotalPaidAmount)} />
                      <Row
                        label="Balance"
                        value={currency(draftBalanceAmount)}
                        valueClassName={
                          isDraftPaymentBalanced ? "text-emerald-700" : "text-rose-600"
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-4 shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPaymentModalOpen(false)}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="button"
                      onClick={applyPaymentModal}
                      className="bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
                      data-testid="pos-payment-apply"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            )}


            {customItemModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
                        <h3 className="font-display text-lg font-bold text-slate-900">
                            Add Custom Restaurant Item
                        </h3>

                        <p className="text-sm text-slate-500 mt-1">
                            Use this only for restaurant sales that are not available in the
                            product list. This will not deduct stock.
                        </p>

                        <div className="space-y-4 mt-5">
                            <div>
                                <Label className="text-xs text-slate-500">
                                    Section
                                </Label>

                                <div className="grid grid-cols-3 gap-2 mt-1">
                                    {Object.values(RESTAURANT_ITEM_SECTIONS).map(
                                        (section) => (
                                            <button
                                                key={section}
                                                type="button"
                                                onClick={() =>
                                                    updateCustomItemForm(
                                                        "itemSection",
                                                        section
                                                    )
                                                }
                                                className={`h-10 rounded-lg border text-xs font-semibold ${
                                                    customItemForm.itemSection ===
                                                    section
                                                        ? "bg-navy-50 border-navy-300 text-navy-800"
                                                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                                                }`}
                                            >
                                                {section}
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs text-slate-500">
                                    Item name
                                </Label>

                                <Input
                                    value={customItemForm.itemName}
                                    onChange={(e) =>
                                        updateCustomItemForm(
                                            "itemName",
                                            e.target.value
                                        )
                                    }
                                    placeholder="Example: Extra chicken portion"
                                    className="mt-1"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs text-slate-500">
                                        Unit price
                                    </Label>

                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={customItemForm.unitPrice}
                                        onChange={(e) =>
                                            updateCustomItemForm(
                                                "unitPrice",
                                                e.target.value
                                            )
                                        }
                                        className="mt-1"
                                    />
                                </div>

                                <div>
                                    <Label className="text-xs text-slate-500">
                                        Quantity
                                    </Label>

                                    <Input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={customItemForm.quantity}
                                        onChange={(e) =>
                                            updateCustomItemForm(
                                                "quantity",
                                                e.target.value
                                            )
                                        }
                                        className="mt-1"
                                    />
                                </div>
                            </div>

                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                                Custom restaurant items are saved as restaurant
                                sales, but no stock batch will be deducted.
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-5">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setCustomItemModalOpen(false)}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="button"
                                onClick={addCustomRestaurantItem}
                                className="bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
                            >
                                Add to Bill
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmDialog && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
                        <h3 className="font-display text-lg font-bold text-slate-900">
                            Confirm Manual Correction
                        </h3>

                        <p className="text-sm text-slate-600 mt-2">
                            This will update the existing sale, recalculate the bill total,
                            restore the previous stock, and deduct stock again using the
                            corrected items.
                        </p>

                        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                            Please confirm only if the corrected bill details are accurate.
                        </div>

                        <div className="mt-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-slate-500">Bill</span>
                                <span className="font-medium">
                                    {sale.saleCode || sale.billNumber}
                                </span>
                            </div>

                            <div className="flex justify-between">
                                <span className="text-slate-500">Items</span>
                                <span className="font-medium">{cart.length}</span>
                            </div>

                            <div className="flex justify-between">
                                <span className="text-slate-500">Grand total</span>
                                <span className="font-bold text-navy-900">
                                    {currency(grandTotal)}
                                </span>
                            </div>

                            <div>
                                <span className="text-slate-500">Reason</span>
                                <p className="mt-1 rounded-md bg-slate-50 border border-slate-200 p-2 text-slate-800">
                                    {reason}
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                disabled={submitting}
                                onClick={() => setShowConfirmDialog(false)}
                                className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                            >
                                Cancel
                            </Button>

                            <Button
                                type="button"
                                disabled={submitting}
                                onClick={confirmManualCorrection}
                                className="bg-amber-500 hover:bg-amber-600 text-navy-900 font-semibold"
                            >
                                {submitting ? "Saving..." : "Confirm Correction"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {!isRestaurantSale && measureBottleModal && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
                        <h3 className="font-display text-lg font-bold text-slate-900">
                            Select Sale Type
                        </h3>

                        <p className="text-sm text-slate-500 mt-1">
                            {measureBottleModal.companyName} {measureBottleModal.bottleName}{" "}
                            {formatContainerAndVolume(measureBottleModal)}
                        </p>

                        <div className="space-y-3 mt-5">
                            <Button
                                type="button"
                                variant="outline"
                                disabled={
                                    Number(measureBottleModal.availableQuantity || 0) <= 0
                                }
                                className="w-full h-auto justify-between px-4 py-3"
                                onClick={() =>
                                    addItem(
                                        measureBottleModal,
                                        "BOTTLE",
                                        BOTTLE_SALE_MODES.FULL_BOTTLE
                                    )
                                }
                            >
                                <div className="text-left">
                                    <div className="font-semibold">Full Bottle</div>

                                    <div className="text-xs text-slate-500">
                                        {Number(measureBottleModal.availableQuantity || 0)} sealed
                                        available
                                    </div>
                                </div>

                                <span className="font-semibold">
                                    {currency(measureBottleModal.sellingPrice)}
                                </span>
                            </Button>

                            <Button
                                type="button"
                                variant="outline"
                                disabled={
                                    getMeasuredMaximumQuantity(
                                        measureBottleModal,
                                        BOTTLE_SALE_MODES.MEASURE_180ML
                                    ) <= 0
                                }
                                className="w-full h-auto justify-between px-4 py-3 border-amber-200 hover:bg-amber-50"
                                onClick={() =>
                                    addItem(
                                        measureBottleModal,
                                        "BOTTLE",
                                        BOTTLE_SALE_MODES.MEASURE_180ML
                                    )
                                }
                            >
                                <div className="text-left">
                                    <div className="font-semibold">180 ml Measure</div>

                                    <div className="text-xs text-slate-500">
                                        Uses 2 × 90 ml units
                                    </div>
                                </div>

                                <span className="font-semibold text-amber-700">
                                    {currency(measureBottleModal.measure180Price)}
                                </span>
                            </Button>

                            <Button
                                type="button"
                                variant="outline"
                                disabled={
                                    getMeasuredMaximumQuantity(
                                        measureBottleModal,
                                        BOTTLE_SALE_MODES.MEASURE_90ML
                                    ) <= 0
                                }
                                className="w-full h-auto justify-between px-4 py-3 border-amber-200 hover:bg-amber-50"
                                onClick={() =>
                                    addItem(
                                        measureBottleModal,
                                        "BOTTLE",
                                        BOTTLE_SALE_MODES.MEASURE_90ML
                                    )
                                }
                            >
                                <div className="text-left">
                                    <div className="font-semibold">90 ml Measure</div>

                                    <div className="text-xs text-slate-500">
                                        Uses 1 measured unit
                                    </div>
                                </div>

                                <span className="font-semibold text-amber-700">
                                    {currency(measureBottleModal.measure90Price)}
                                </span>
                            </Button>
                        </div>

                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mt-4 text-xs text-slate-600">
                            <div>
                                Opened bottle units:{" "}
                                <span className="font-semibold">
                                    {Number(measureBottleModal.availableMeasureUnits || 0)}
                                </span>
                            </div>

                            <div className="mt-1">
                                Sealed bottles:{" "}
                                <span className="font-semibold">
                                    {Number(measureBottleModal.availableQuantity || 0)}
                                </span>
                            </div>
                        </div>

                        <Button
                            type="button"
                            variant="outline"
                            className="w-full mt-3"
                            onClick={() => setMeasureBottleModal(null)}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

const ProductImageBox = ({
  product,
  isBottle,
  isBeverage,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  const hasImage = Boolean(product.imageUrl) && !imageFailed;

  return (
    <div className="h-52 w-full sm:h-56 sm:w-44 md:w-48 shrink-0 rounded-xl border border-slate-200 bg-white p-2 overflow-hidden grid place-items-center">
      {hasImage ? (
        <img
          src={product.imageUrl}
          alt={isBottle ? product.bottleName : product.biteName}
          className="block max-h-full max-w-full object-contain"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className="h-full w-full rounded-lg bg-slate-50 grid place-items-center">
          <div
            className={`h-12 w-12 rounded-xl grid place-items-center ${
              isBottle
                ? "bg-rose-50 text-rose-700"
                : isBeverage
                  ? "bg-sky-50 text-sky-700"
                  : "bg-yellow-50 text-yellow-700"
            }`}
          >
            {isBottle ? (
              <Wine className="h-6 w-6" />
            ) : isBeverage ? (
              <Coffee className="h-6 w-6" />
            ) : (
              <Utensils className="h-6 w-6" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, valueClassName = "" }) => (
    <div className="flex items-center justify-between gap-3">
        <span className="text-slate-500">{label}</span>
        <span className={`font-medium text-right break-words ${valueClassName}`}>
            {value}
        </span>
    </div>
);

const NumberInput = ({ label, value, onChange }) => (
    <div>
        <Label className="text-[10px] text-slate-500 uppercase tracking-wide">
            {label}
        </Label>

        <Input
            type="number"
            value={value}
            onChange={(e) =>
                onChange(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="h-9"
        />
    </div>
);

const PaymentInfoBox = ({ icon, title, description }) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
            {icon}

            <div>
                <div className="text-xs font-semibold text-slate-800">
                    {title}
                </div>

                <div className="text-[11px] text-slate-500 mt-0.5">
                    {description}
                </div>
            </div>
        </div>
    </div>
);

const ServiceChargeToggle = ({ enabled, onToggle }) => (
    <div>
        <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Service 10%</Label>
        <button type="button" aria-pressed={enabled} onClick={onToggle}
            className={`h-9 w-full rounded-md border px-3 text-sm font-semibold ${enabled ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-input bg-background text-slate-600"}`}>
            Apply
        </button>
    </div>
);
